import { IconButton } from "@itk/mui-components";
import { Autocomplete, CircularProgress, InputAdornment, Stack, TextField, Typography, useTheme, Drawer } from "@mui/material";
import { GridColDef, GridRowParams, GridSortModel } from "@mui/x-data-grid-pro";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { usePriceListPageT } from "src/translation-hooks/price-list-page";
import CloseIcon from "@mui/icons-material/Close";
import { genericTranslationsK } from "src/translation-hooks/generic-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { priceListActions } from "./price-list-store";
import { PriceListTableColumns } from "./price-list-types";
import { DataGridWithIdentity, DekoxIcon } from "src/components";
import { Model_PriceListPost } from "src/backend/openapi/v1";
import { useEffectAfterMount } from "src/utils/hooks/useEffectAfterMount";
import { useCurrencyTranslation } from "src/translations-enums-hooks/useCurrencyTranslation";
import { thousandsSeparator } from "src/utils";
import { useUserSettings } from "src/hooks";
import { applicationActions } from "src/components/application-store";
import { IDENTITY_SETTINGS_KEYS } from "src/utils/types/identity-settings-types";
import { hasValue } from "@itk/typescript-utils";
import { GenericListSortingDirection } from "src/components/datagrid-with-identity/datagrid-with-identity-types";
import { ProductDetailsDrawer } from "./components/product-details-drawer/product-details-drawer";

const mapStateToProps = (state: RootState) => ({
  priceListState: state.pageReducer.priceListReducer.priceListState,
  productTypes: state.pageReducer.priceListReducer.productTypes,
  identitySettings: state.generalReducer.applicationReducer.identitySettings
});

const ownActions = {
  getPriceListRequest: priceListActions.getPriceListRequest,
  getProductTypesRequest: priceListActions.getProductTypesRequest,
  setIdentitySettingsRequest: applicationActions.setIdentitySettingsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const PriceListBase = (props: Props) => {
  const { getPriceListRequest, priceListState, getProductTypesRequest, productTypes, identitySettings, setIdentitySettingsRequest } = props;

  const priceListPageT = usePriceListPageT();
  const theme = useTheme();
  const { language } = useUserSettings();
  const locale = language === "da" ? "da-dk" : "en";

  const dataGridSettings = identitySettings[IDENTITY_SETTINGS_KEYS.PRICE_LIST_DATA_GRID];

  const { getCurrencyTranslation } = useCurrencyTranslation();

  const [search, setSearch] = useState("");
  const [debounceSearch, setDebounceSearch] = useState("");
  const [selectedProductType, setSelectedProductType] = useState<{ label: string; value: string } | null>();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: dataGridSettings?.pageSize ? Number(dataGridSettings.pageSize) : 50,
    page: 0
  });
  const [sortModel, setSortModel] = useState<GridSortModel[number] | undefined>(
    dataGridSettings && hasValue(dataGridSettings?.sortColumnId) && hasValue(dataGridSettings?.sortDirection)
      ? {
          field: dataGridSettings.sortColumnId,
          sort: dataGridSettings.sortDirection === GenericListSortingDirection.Ascending ? "asc" : "desc"
        }
      : undefined
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const getPriceList = () => {
    const payload: Model_PriceListPost = {
      ...paginationModel,
      sortColumnId: sortModel?.field,
      sortDirection: sortModel?.sort && sortModel?.sort === "asc" ? GenericListSortingDirection.Ascending : GenericListSortingDirection.Descending,
      wildcardSearch: debounceSearch
    };
    if (selectedProductType) {
      payload["productTypeId"] = selectedProductType.value;
    }
    getPriceListRequest(payload);
  };

  useEffect(() => {
    if (priceListState.loading === "has-loaded" && !hasLoaded) setHasLoaded(true);
  }, [priceListState.loading]);

  useEffect(() => {
    getPriceList();
  }, []);

  useEffectAfterMount(() => {
    getPriceList();
  }, [paginationModel.page, paginationModel.pageSize, sortModel?.field, sortModel?.sort, debounceSearch, selectedProductType?.value]);

  useEffect(() => {
    if (productTypes.loading === "not-started") {
      getProductTypesRequest();
    }
  }, [productTypes]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounceSearch(search);
    }, 500);

    return () => clearTimeout(handler);
  }, [search]);

  const handleRowClick = (productId: string) => {
    setSelectedProductId(productId);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedProductId(null);
  };

  const columns: GridColDef<PriceListTableColumns>[] = [
    {
      field: "productNumber",
      headerName: priceListPageT.productNumber,
      flex: 1
    },
    { field: "productName", headerName: priceListPageT.productName, flex: 1 },
    { field: "productTypeName", headerName: priceListPageT.productType, flex: 1 },
    { field: "currency", headerName: priceListPageT.currency, flex: 0.5, renderCell: value => <Typography>{getCurrencyTranslation(value.row.currencyId)}</Typography> },
    { field: "productUnitOfMeasure", headerName: priceListPageT.unitOfMeasure, flex: 0.5 },
    {
      field: "unitPrice",
      headerName: priceListPageT.unitPrice,
      flex: 0.5,
      align: "right",
      headerAlign: "right",
      renderCell: params => <Typography>{thousandsSeparator(params.row.unitPrice, locale)}</Typography>
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Stack direction="row" justifyContent="flex-end" flex={1}>
          <IconButton size="small" k={genericTranslationsK.delete} onClick={() => handleRowClick(params.row.productId)}>
            <DekoxIcon icon="arrow-right" />
          </IconButton>
        </Stack>
      )
    }
  ];

  return (
    <Stack spacing={2} flex={1} overflow="auto" width="100%" sx={{ background: theme.palette.customColors.lightGrey }}>
      {!hasLoaded && (
        <Stack flex={1} justifyContent="center" alignItems="center">
          <CircularProgress />
        </Stack>
      )}

      {hasLoaded && (
        <>
          <Typography color={theme.palette.customColors.darkGrey} variant="h2" fontWeight={theme.typography.fontWeightBold}>
            {priceListPageT.pricelist}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" gap={2}>
              <TextField
                fullWidth
                autoFocus
                placeholder={priceListPageT.searchInPriceList}
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                sx={{
                  minWidth: 450,
                  "& .MuiInputBase-root": {
                    paddingY: 0.5
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 1 }}>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton k={genericTranslationsK.clear} onClick={() => setSearch("")}>
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />
              <Autocomplete
                value={selectedProductType}
                onChange={(_, data) => setSelectedProductType(data)}
                getOptionKey={item => `${item.label}-${item.value}`}
                options={productTypes.loading === "has-loaded" ? productTypes.data.map(item => ({ label: item.name, value: item.productTypeId })).sort() : []}
                renderInput={params => <TextField {...params} size="small" placeholder={priceListPageT.productCategory} variant="outlined" />}
                sx={{
                  minWidth: 300,
                  "& .MuiFormControl-root .MuiInputBase-root": {
                    padding: 1.25
                  }
                }}
              />
            </Stack>
          </Stack>
          <DataGridWithIdentity
            dataGridSettings={dataGridSettings}
            handleSaveSettings={value => setIdentitySettingsRequest(IDENTITY_SETTINGS_KEYS.PRICE_LIST_DATA_GRID, JSON.stringify(value))}
            key={priceListState.loading}
            rows={priceListState.loading === "has-loaded" ? priceListState.data.data : undefined}
            rowCount={priceListState.loading === "has-loaded" ? priceListState.data.totalCount : 0}
            columns={columns}
            getRowId={row => row.priceId}
            onRowDoubleClick={(params: GridRowParams<PriceListTableColumns>) => handleRowClick(params.row.productId)}
            onCellKeyDown={(rowEvent, e) => {
              if (e.key === "Enter" && rowEvent.colDef.field !== "actions") handleRowClick(rowEvent.row.productId);
            }}
            loading={priceListState.loading === "is-loading"}
            sortModel={sortModel ? [sortModel] : undefined}
            onSortModelChange={model => {
              setSortModel(model[0]);
            }}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={model => {
              setPaginationModel(model);
            }}
            paginationMode="server"
            pageSizeOptions={[
              { label: "5", value: 5 },
              { label: "10", value: 10 },
              { label: "20", value: 20 },
              { label: "50", value: 50 },
              { label: "100", value: 100 }
            ]}
            sx={{
              fontSize: theme.typography.subtitle1.fontSize,
              overflow: "auto",
              color: theme.palette.customColors.darkGrey,
              background: theme.palette.customColors.white,
              borderRadius: 1,
              fontWeight: "normal",
              "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel": {
                fontWeight: theme.typography.subtitle1.fontWeight,
                fontSize: theme.typography.subtitle1.fontSize
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: theme.typography.fontWeightBold
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "hidden"
              }
            }}
          />

          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleCloseDrawer}
            sx={{
              "& .MuiDrawer-paper": {
                width: 360,
                display: "flex",
                flexDirection: "column"
              }
            }}
          >
            {selectedProductId && <ProductDetailsDrawer productId={selectedProductId} onClose={handleCloseDrawer} />}
          </Drawer>
        </>
      )}
    </Stack>
  );
};

export const PriceList = connect(mapStateToProps, ownActions)(PriceListBase);
